/**
 * Which layers are drawn.
 *
 * A view setting only — none of this is ever saved. The document always holds every feature
 * it loaded, whatever the admin has hidden (DESK_RETIREMENT_PLAN.md 2.1).
 *
 * Two controls, not three: a layer is shown or not, and one layer can be soloed. A per-layer
 * opacity slider was here and has been taken out — it is a control that looks powerful and
 * answers a question nobody was asking, while making every row twice as tall and the panel
 * slower to read. Solo already does the thing it was for: see one layer without the rest.
 */

export type EditorLayerKey =
  | 'floor'
  | 'rooms'
  | 'border'
  | 'desks'
  | 'deskShapes'
  | 'labels'
  | 'retiredDesks';

export type LayerVisibility = Readonly<Record<EditorLayerKey, boolean>>;

export type LayerSettings = Readonly<Record<EditorLayerKey, { readonly visible: boolean }>>;

/** Panel order: bottom of the drawing first, matching how the plan is painted. */
export const EDITOR_LAYER_KEYS: readonly EditorLayerKey[] = [
  'floor',
  'rooms',
  'border',
  'deskShapes',
  'desks',
  'labels',
  'retiredDesks',
];

/**
 * Retired desks are a filter on the desks layer, not a layer of their own.
 *
 * They are drawn by the desk painter, from the same collection, so they have no opacity of
 * their own to set and nothing to solo — souloing them alone would hide the desks layer that
 * draws them and show an empty plan. The panel therefore offers them a visibility box only.
 */
export const isSoloableLayer = (key: EditorLayerKey): boolean => key !== 'retiredDesks';

export const DEFAULT_LAYER_SETTINGS: LayerSettings = {
  floor: { visible: true },
  rooms: { visible: true },
  border: { visible: true },
  deskShapes: { visible: true },
  desks: { visible: true },
  labels: { visible: true },
  // The one layer that starts hidden. A desk whose retirement date has arrived is gone from
  // every booking surface, so drawing it here by default would show a floor that no longer
  // exists — but it stays one click away, because cancelling a retirement means selecting
  // the desk.
  retiredDesks: { visible: false },
};

/**
 * What is actually drawn, with solo resolved.
 *
 * Solo wins over the individual boxes rather than rewriting them: it is a "show me only
 * this" gesture, and an admin who solos a layer to look at it expects their own visibility
 * choices back untouched when they turn it off. Retired desks keep their own setting either
 * way — they are part of the desks layer, so soloing desks does not silently reveal the
 * desks that are out of service.
 */
export const visibilityOf = (
  settings: LayerSettings,
  solo: EditorLayerKey | null,
): LayerVisibility => {
  const visible = (key: EditorLayerKey): boolean => (
    solo === null ? settings[key].visible : key === solo
  );

  return {
    floor: visible('floor'),
    rooms: visible('rooms'),
    border: visible('border'),
    deskShapes: visible('deskShapes'),
    desks: visible('desks'),
    labels: visible('labels'),
    // Still gated on the desks layer being drawn at all: the desk painter is what draws
    // them, so claiming they are visible while desks are hidden would be a lie the
    // hit-tester believes — and it would make a retired desk clickable out of empty floor.
    retiredDesks: settings.retiredDesks.visible && visible('desks'),
  };
};

export const withVisible = (
  settings: LayerSettings,
  key: EditorLayerKey,
  visible: boolean,
): LayerSettings => ({ ...settings, [key]: { visible } });

/** Solo is a radio, not a checkbox: soloing the layer already soloed turns it off. */
export const nextSolo = (
  current: EditorLayerKey | null,
  key: EditorLayerKey,
): EditorLayerKey | null => (current === key ? null : key);

/**
 * Whether any layer is hidden.
 *
 * Drives the "you are not looking at the whole plan" hint. A plan with its floor layer
 * switched off looks like a plan whose floor failed to load, and an admin who set that ten
 * minutes ago has no reason to connect the two.
 */
export const isFiltered = (settings: LayerSettings, solo: EditorLayerKey | null): boolean => (
  solo !== null || EDITOR_LAYER_KEYS.some((key) => key !== 'retiredDesks' && !settings[key].visible)
);
